import { useState } from "react";
import { Plus } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { clearMentions, unreadFor, useMentions } from "@/estimating/lib/mentions";

// Pipeline notes: "MM/YY text" chips in two rows — read down then right (1 over 2, 3 over 4…); "+" follows the newest.
const mmyy = (d) => `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(-2)}`;
const stampOf = (n) => (n.at ? mmyy(new Date(n.at)) : "");

const Chip = ({ n, testid, onOpen }) => (
  <HoverCard openDelay={200} onOpenChange={(o) => o && onOpen()}>
    <HoverCardTrigger asChild>
      <span className="inline-flex max-w-[180px] shrink-0 cursor-default items-center gap-1 rounded bg-slate-100 px-1.5 text-[11px] leading-4 text-slate-700" data-testid={testid}>
        {stampOf(n) && <span className="font-semibold tabular-nums text-slate-500">{stampOf(n)}</span>}
        <span className="truncate">{n.text}</span>
      </span>
    </HoverCardTrigger>
    <HoverCardContent align="start" className="w-72 p-2.5 text-xs" data-testid={`${testid}-hover`}>
      <div className="text-[10px] text-muted-foreground">
        {n.at ? `${n.user || ""} · ${new Date(n.at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : "Earlier note"}
      </div>
      <div className="mt-0.5 whitespace-pre-wrap text-foreground">{n.text}</div>
    </HoverCardContent>
  </HoverCard>
);

/** notes: oldest first [{text, user, initials, at}]. onAdd(text) → truthy when saved. */
export default function NoteChips({ notes = [], onAdd, testid, target, canAdd = true }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
    const { items } = useMentions();
  const lit = target ? unreadFor(items, target) > 0 : false;
  const seen = () => target && lit && clearMentions(target);
  const post = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try { if (await onAdd(text.trim())) { setText(""); setAdding(false); } } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-flow-col grid-rows-2 items-center justify-start gap-x-1 gap-y-px" data-testid={testid}
         data-mention={lit ? "1" : undefined}>
      {notes.map((n, i) => <Chip key={i} n={n} testid={`${testid}-chip-${i}`} onOpen={seen} />)}
      {adding && (
        <span className="inline-flex shrink-0 items-center gap-1 rounded border border-[#007BFF] px-1.5 text-[11px] leading-[14px]">
          <span className="font-semibold tabular-nums text-slate-500" data-testid={`${testid}-stamp`}>{mmyy(new Date())}</span>
          <input autoFocus value={text} onChange={(e) => setText(e.target.value)} disabled={busy} placeholder="Note…"
                 className="w-40 bg-transparent text-[11px] outline-none" data-testid={`${testid}-input`}
                 onKeyDown={(e) => { if (e.key === "Enter") post(); if (e.key === "Escape") { setText(""); setAdding(false); } }}
                 onBlur={() => !text.trim() && setAdding(false)} />
        </span>)}
      {canAdd && !adding && (
        <button type="button" onClick={() => { seen(); setAdding(true); }} title="Add note" data-testid={`${testid}-add`}
                className={`relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#007BFF] ${lit ? "text-[#007BFF]" : ""}`}>
          <Plus className="h-3.5 w-3.5" />
          {lit && <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-[#007BFF]" />}
        </button>)}
      {!canAdd && !notes.length && <span className="text-slate-400">—</span>}
    </div>
  );
}
